import React, { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Pill from 'carbon-react/lib/components/pill';
import Icon from 'carbon-react/lib/components/icon';

import { DispatchedReportItem } from '@futrli-sdk/report_scheduling';
import { FormattedMessage, useIntl } from '@futrli/localisation';
import { getReportingEditReportUrl, matchConsolidationDetailRoutePath } from '@futrli/shared-services';
import { ScheduleListItem, ScheduleRepeat } from '@futrli/views/processors/reportScheduling/reportSchedulingList';

import { openPdfForViewing } from '../../../utils/pdf-actions';
import { ListingPageRowLayout } from '@futrli/ui-components/components/listing-page/listing-page-row/listing-page-row-layout/listing-page-row-layout';
import { ListingPageRowNameCell } from '@futrli/ui-components/components/listing-page/listing-page-row/listing-page-row-name-cell/listing-page-row-name-cell';
import { ListingPageRowMetricCell } from '@futrli/ui-components/components/listing-page/listing-page-row/listing-page-row-metric-cell/listing-page-row-metric-cell';
import { ScheduleActionsPopover } from './popovers/schedule-actions-popover';
import { ScheduleReviewPopover } from './popovers/schedule-review-popover';

interface ScheduleReportRowProps {
  item: ScheduleListItem;
  isResourceSharingEnabled?: boolean;
  onApprove: (item: ScheduleListItem) => void;
  onDeny: (item: ScheduleListItem) => void;
  onDelete: (item: ScheduleListItem) => void;
}

const StatusPill = ({ status }: { status: ScheduleListItem['derivedStatus'] }) => {
  const intl = useIntl();

  switch (status) {
    case 'scheduled':
      return (
        <Pill colorVariant="positive" size="S" fill>
          {intl.formatMessage({ id: 'predict.scheduled', defaultMessage: 'Scheduled' })}
        </Pill>
      );
    case 'awaiting-review':
      return (
        <Pill pillRole="status" colorVariant="information" size="S" fill>
          {intl.formatMessage({ id: 'reporting.awaiting_review', defaultMessage: 'Awaiting review' })}
        </Pill>
      );
    case 'expired':
      return (
        <Pill pillRole="status" size="S" fill>
          {intl.formatMessage({ id: 'common.expired', defaultMessage: 'Expired' })}
        </Pill>
      );
    default:
      return null;
  }
};

const DASH = '–';
const DATE_FORMAT = { day: '2-digit', month: 'short', year: 'numeric' } as const;
type StaticScheduleRepeatKey = Exclude<ScheduleRepeat['key'], 'every-days' | 'every-weeks' | 'every-months'>;

const REPEAT_MESSAGES: Record<StaticScheduleRepeatKey, { id: string; defaultMessage: string }> = {
  'daily': { id: 'common.recurrence_daily', defaultMessage: 'Daily' },
  'monthly': { id: 'common.monthly', defaultMessage: 'Monthly' },
  'weekly': { id: 'common.weekly', defaultMessage: 'Weekly' },
  'does-not-repeat': { id: 'reporting.recurrence_does_not_repeat', defaultMessage: 'Does not repeat' },
  'quarterly': { id: 'common.quarterly', defaultMessage: 'Quarterly' },
  'annually': { id: 'common.annually', defaultMessage: 'Annually' }
};

const formatDate = (intl: ReturnType<typeof useIntl>, date: Date | null): string =>
  date ? intl.formatDate(date, DATE_FORMAT) : DASH;

const formatRepeat = (intl: ReturnType<typeof useIntl>, repeat: ScheduleRepeat | null): string => {
  if (repeat === null) {
    return DASH;
  }

  if (repeat.key === 'every-days') {
    return intl.formatMessage(
      { id: 'reporting.recurrence_every_days', defaultMessage: 'Every {count} days' },
      { count: repeat.count }
    );
  }

  if (repeat.key === 'every-weeks') {
    return intl.formatMessage(
      { id: 'reporting.recurrence_every_weeks', defaultMessage: 'Every {count} weeks' },
      { count: repeat.count }
    );
  }

  if (repeat.key === 'every-months') {
    return intl.formatMessage(
      { id: 'reporting.recurrence_every_months', defaultMessage: 'Every {count} months' },
      { count: repeat.count }
    );
  }

  return intl.formatMessage(REPEAT_MESSAGES[repeat.key]);
};

const formatReviewStep = (intl: ReturnType<typeof useIntl>, reviewRequired: boolean | null): string => {
  if (reviewRequired === null) {
    return DASH;
  }

  if (reviewRequired) {
    return intl.formatMessage({ id: 'predict.review_required', defaultMessage: 'Required' });
  }

  return intl.formatMessage({ id: 'predict.review_none', defaultMessage: 'None' });
};

const formatRecipientCount = (intl: ReturnType<typeof useIntl>, recipientCount: number | null): string => {
  if (recipientCount === null) {
    return DASH;
  }

  return intl.formatMessage(
    { id: 'predict.users_count', defaultMessage: '{count} User(s)' },
    { count: recipientCount }
  );
};

export const ScheduleReportRow = ({
  item,
  isResourceSharingEnabled = false,
  onApprove,
  onDeny,
  onDelete
}: ScheduleReportRowProps) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { workspaceId = '', organisationId = '' } = useParams();
  const isConsolidation = !!matchConsolidationDetailRoutePath(window.location.pathname);

  const isAwaitingReview = item.derivedStatus === 'awaiting-review';
  const sourceItem = item.sourceItem;
  const createdBy = item.createdByName ?? DASH;
  const [isViewing, setIsViewing] = useState(false);

  const { sendDate: sendDateValue, endDate, repeat, reviewRequired, recipientCount } = item.columns;
  const sendDate = formatDate(intl, sendDateValue);
  const repeatText = formatRepeat(intl, repeat);
  const ends = formatDate(intl, endDate);
  const reviewStep = formatReviewStep(intl, reviewRequired);
  const sendTo = formatRecipientCount(intl, recipientCount);

  const handleNameClick = useCallback(() => {
    if (item.reportId) {
      navigate(getReportingEditReportUrl(workspaceId, organisationId, item.reportId, isConsolidation));
    }
  }, [navigate, workspaceId, organisationId, item.reportId, isConsolidation]);

  const handleViewPdf = useCallback(async () => {
    if (item.source !== 'dispatched') {
      return;
    }
    const awaitingReviewReport = sourceItem as DispatchedReportItem;
    if (awaitingReviewReport.download_link && awaitingReviewReport.name) {
      try {
        setIsViewing(true);
        await openPdfForViewing(awaitingReviewReport.download_link);
      } finally {
        setIsViewing(false);
      }
    }
  }, [sourceItem, item.source]);

  const handleApproveClick = useCallback(() => {
    if (item.source === 'dispatched') {
      onApprove(item);
    }
  }, [item, onApprove]);

  const handleDenyClick = useCallback(() => {
    if (item.source === 'dispatched') {
      onDeny(item);
    }
  }, [item, onDeny]);

  const handleDeleteClick = useCallback(() => {
    onDelete(item);
  }, [item, onDelete]);

  return (
    <ListingPageRowLayout
      // eslint-disable-next-line no-magic-numbers
      nameColumnFlexBasis={isResourceSharingEnabled ? 250 : undefined}
      nameColumn={
        isAwaitingReview ? (
          <ListingPageRowNameCell
            name={
              <>
                <Icon type="file_pdf" mr="8px" />
                {item.name}
              </>
            }
            onNameClick={() => {
              if (!isViewing) {
                handleViewPdf();
              }
            }}
          />
        ) : (
          <ListingPageRowNameCell name={item.name} onNameClick={handleNameClick} />
        )
      }
      metrics={
        <>
          <ListingPageRowMetricCell
            size="status"
            label={<FormattedMessage id="common.status" defaultMessage="Status" />}
            value={<StatusPill status={item.derivedStatus} />}
          />
          {isResourceSharingEnabled && (
            <ListingPageRowMetricCell
              size="status"
              label={<FormattedMessage id="reporting.created_by" defaultMessage="Created by" />}
              value={createdBy}
            />
          )}
          <ListingPageRowMetricCell
            size="medium"
            label={<FormattedMessage id="predict.send_date" defaultMessage="Send date" />}
            value={sendDate}
          />
          <ListingPageRowMetricCell
            label={<FormattedMessage id="common.repeat" defaultMessage="Repeat" />}
            value={repeatText}
          />
          <ListingPageRowMetricCell label={<FormattedMessage id="common.ends" defaultMessage="Ends" />} value={ends} />
          <ListingPageRowMetricCell
            label={<FormattedMessage id="reporting.review_step" defaultMessage="Review step" />}
            value={reviewStep}
          />
          <ListingPageRowMetricCell
            label={<FormattedMessage id="predict.send_to" defaultMessage="Send to" />}
            value={sendTo}
          />
        </>
      }
      actions={
        <>
          <ScheduleReviewPopover isHidden={!isAwaitingReview} onApprove={handleApproveClick} onDeny={handleDenyClick} />

          <ScheduleActionsPopover
            status={item.derivedStatus}
            awaitingReviewItem={isAwaitingReview ? (sourceItem as DispatchedReportItem) : undefined}
            reportId={item.reportId}
            reportName={item.name}
            scheduleId={
              item.source === 'schedule' ? sourceItem.id : (sourceItem as DispatchedReportItem).report_schedule_id
            }
            onDelete={handleDeleteClick}
          />
        </>
      }
    />
  );
};
